import React, { forwardRef, useState, useEffect, useRef } from 'react';
import { Mic, CheckCircle, RotateCcw, FileText, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { VoiceToSoapRecorderWebSocket, VoiceToSoapRecorderHandle, ValidationStageEvent } from '@/components/VoiceToSoapRecorderWebSocket';
import type { VoiceToSoapResponse } from '@/services/api/medicalRecordsApi';

type InputMode = 'voice' | 'text';

interface VoiceRecorderSectionProps {
  patientId: string;
  onSoapGenerated: (response: VoiceToSoapResponse & { __mode?: 'refine' | 'replace' | 'partial' }) => void;
  onError: (error: string) => void;
  onProcessText?: (text: string) => void;
  onValidationStage?: (event: ValidationStageEvent) => void;
  collapsed?: boolean;
}

export const VoiceRecorderSection = forwardRef<VoiceToSoapRecorderHandle, VoiceRecorderSectionProps>(
  ({ patientId, onSoapGenerated, onError, onProcessText, onValidationStage, collapsed = false }, ref) => {
    const [lastRecordingDuration, setLastRecordingDuration] = useState<number>(0);
    const [recordingStartTime, setRecordingStartTime] = useState<number | null>(null);
    const [isReRecording, setIsReRecording] = useState(false);
    const [inputMode, setInputMode] = useState<InputMode>('voice');
    const [pastedText, setPastedText] = useState('');
    const [isProcessingText, setIsProcessingText] = useState(false);

    // Internal ref to access the WebSocket recorder when external ref isn't available
    const internalRef = useRef<VoiceToSoapRecorderHandle>(null);
    const recorderRef = (ref as React.RefObject<VoiceToSoapRecorderHandle>) || internalRef;

    // Track recording duration
    useEffect(() => {
      if (collapsed && recordingStartTime) {
        setLastRecordingDuration(Math.floor((Date.now() - recordingStartTime) / 1000));
        setRecordingStartTime(null);
      }
    }, [collapsed, recordingStartTime]);

    const handleSoapGenerated = (response: VoiceToSoapResponse & { __mode?: 'refine' | 'replace' | 'partial' }) => {
      // Forward partial streaming updates without resetting UI state
      if (response.__mode === 'partial') {
        onSoapGenerated(response);
        return;
      }
      if (recordingStartTime) {
        setLastRecordingDuration(Math.floor((Date.now() - recordingStartTime) / 1000));
      }
      setIsReRecording(false);
      setIsProcessingText(false);
      onSoapGenerated(response);
    };

    const handleProcessText = () => {
      if (!pastedText.trim()) return;
      setIsProcessingText(true);
      if (onProcessText) {
        // Use the parent handler which sets regenerating mode for full replace
        onProcessText(pastedText.trim());
      } else if (recorderRef.current) {
        // Fallback to direct call
        recorderRef.current.regenerateFromTranscript(pastedText.trim());
      }
      // Safety timeout in case response never arrives
      setTimeout(() => {
        setIsProcessingText((wasProcessing) => {
          if (wasProcessing) {
            onError('Text processing timed out. Please check your connection and try again.');
          }
          return false;
        });
      }, 60000);
    };

    const handleError = (error: string) => {
      setIsProcessingText(false);
      onError(error);
    };

    const formatDuration = (seconds: number): string => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const isCollapsed = collapsed && !isReRecording;

    return (
      <>
        {/* Collapsed state after SOAP generation */}
        {isCollapsed && (
          <div
            className="rounded-lg border p-4 flex items-center justify-between"
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border-primary)',
            }}
          >
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: 'color-mix(in srgb, var(--color-success) 15%, transparent)' }}
              >
                <CheckCircle className="w-5 h-5" style={{ color: 'var(--color-success)' }} />
              </div>
              <div>
                <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  {inputMode === 'text' ? 'Text processed' : 'Recording complete'}
                </span>
                {lastRecordingDuration > 0 && inputMode === 'voice' && (
                  <span className="text-xs ms-2 font-mono" style={{ color: 'var(--color-text-tertiary)' }}>
                    ({formatDuration(lastRecordingDuration)})
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsReRecording(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors hover:opacity-80"
              style={{
                color: 'var(--color-primary-600)',
                backgroundColor: 'var(--color-primary-50)',
              }}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              {inputMode === 'text' ? 'Re-process' : 'Re-record'}
            </button>
          </div>
        )}

        {/* WebSocket recorder - kept mounted when collapsed to preserve connection for refinements */}
        <div
          className={isCollapsed ? 'sr-only' : 'rounded-lg border p-5'}
          style={isCollapsed ? undefined : {
            backgroundColor: 'var(--color-bg-secondary)',
            borderColor: 'var(--color-border-primary)',
          }}
          aria-hidden={isCollapsed}
        >
          {!isCollapsed && (
            <>
              {/* Mode tabs */}
              <div className="flex items-center gap-1 mb-4 p-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
                <button
                  type="button"
                  onClick={() => setInputMode('voice')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex-1 justify-center"
                  style={{
                    backgroundColor: inputMode === 'voice' ? 'var(--color-bg-primary)' : 'transparent',
                    color: inputMode === 'voice' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)',
                    boxShadow: inputMode === 'voice' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  }}
                >
                  <Mic className="w-4 h-4" />
                  Voice Recording
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('text')}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex-1 justify-center"
                  style={{
                    backgroundColor: inputMode === 'text' ? 'var(--color-bg-primary)' : 'transparent',
                    color: inputMode === 'text' ? 'var(--color-primary-600)' : 'var(--color-text-secondary)',
                    boxShadow: inputMode === 'text' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                  }}
                >
                  <FileText className="w-4 h-4" />
                  Paste Text
                </button>
              </div>
            </>
          )}

          {/* Voice mode — show the WebSocket recorder */}
          <div className={!isCollapsed && inputMode !== 'voice' ? 'hidden' : undefined}>
            <VoiceToSoapRecorderWebSocket
              ref={ref}
              patientId={patientId}
              onSoapGenerated={(response) => {
                if (!recordingStartTime) {
                  setRecordingStartTime(Date.now());
                }
                handleSoapGenerated(response);
              }}
              onError={handleError}
              onValidationStage={onValidationStage}
              soapSection="full"
              disabled={!patientId}
            />
          </div>

          {/* Text mode — paste/type clinical notes */}
          {!isCollapsed && inputMode === 'text' && (
            <div className="space-y-3">
              <Textarea
                value={pastedText}
                onChange={(e) => setPastedText(e.target.value)}
                placeholder="Paste or type clinical notes, old documentation, or a transcript here..."
                className="text-sm resize-y"
                style={{ minHeight: '150px' }}
                disabled={isProcessingText || !patientId}
                rows={8}
              />

              <div className="flex items-center justify-between">
                <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                  {!patientId
                    ? 'Please select a patient first'
                    : 'Paste clinical notes, discharge summaries, or any medical text to extract a structured SOAP note'}
                </p>
                <Button
                  type="button"
                  onClick={handleProcessText}
                  disabled={isProcessingText || !pastedText.trim() || !patientId}
                  loading={isProcessingText}
                >
                  {!isProcessingText && <Send />}
                  {isProcessingText ? 'Processing...' : 'Generate SOAP'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </>
    );
  }
);

VoiceRecorderSection.displayName = 'VoiceRecorderSection';
